import i18n from "@/Plugins/i18n";

import type { EventFilter } from "@/Modules/Chat/WebSocketManager";
import type { AllowedChatRoomItemType, TextChatRoomItem } from "@/Modules/Chat/Models/Domain/Chat";
import type { TextChatRoomItemMessageWebSocket } from "@/Modules/Chat/Models/WebSocket";
import type { ChatStore } from "@/Modules/Chat/Store";
import type { UserStore } from "@/Modules/User/Store";
import { ChatRoomItemTypeMessageWebSocket } from "@/Modules/Chat/Models/WebSocket";
import { ChatRoomItemType } from "@/Modules/Chat/Models/Domain/Chat";

import useAppStore, { type AppStore } from "@/Modules/App/Store";
import useChatStore from "@/Modules/Chat/Store";
import useUserStore from "@/Modules/User/Store";

import { APP_ROUTES } from "@/Generated/Routes/paths";

import { addNewEventWithNeutrals } from "@/Modules/Chat/MessageFilters/helpers/AddNeutralDateEvent";
import { unixSecondsToDate } from "@/Utils/Date";
import { hasValue, isObject } from "@/Utils/Objects";
import { isApp } from "@/Utils/Ionic";

export interface ExtraParamsTextChatRoomItem {
  chatStore: ChatStore;
  userStore: UserStore;
  appStore: AppStore;
}

export const isTextChatRoomItemType = (message: unknown): message is TextChatRoomItemMessageWebSocket => {
  return isObject(message) && "etype" in message && message.etype === ChatRoomItemTypeMessageWebSocket.TEXT;
};

export const textChatRoomItemHandler = (
  message: TextChatRoomItemMessageWebSocket,
  {
    chatStore = useChatStore(),
    userStore = useUserStore(),
    appStore = useAppStore(),
  }: ExtraParamsTextChatRoomItem
) => {
  if (chatStore.rooms[message.chat] !== undefined) {
    const textChatRoomItem = transformTextChatRoomItemMessageWebSocketToTextChatRoomItem(
      message,
      userStore.user?.id
    );
    const events = hasValue(chatStore.rooms[message.chat])
      ? addNewEventWithNeutrals(chatStore.rooms[message.chat]!.events, textChatRoomItem)
      : [];
    let unreadEvents = 0;
    let i = events.length - 1;

    while (i > 0) {
      const event = events[i--]!;

      if (
        event.type === ChatRoomItemType.INCOMING_TEXT &&
        (event as AllowedChatRoomItemType).isReceived === false &&
        !event.old
      ) {
        unreadEvents++;
      } else if (
        event.type !== ChatRoomItemType.OUTGOING_TEXT &&
        (event as AllowedChatRoomItemType).isReceived === true
      ) {
        i = -1;
      }
    }

    if (hasValue(chatStore.rooms[message.chat])) {
      if (!message.old) {
        appStore.notificate({
          title: chatStore.rooms[message.chat]!.user.username,
          body: isApp() ? message.text : i18n.global.t("browser-notifications.new_message"),
          redirect: userStore.getUserIsTherapist ? { name: APP_ROUTES.chat.root.name } : undefined,
          extraParams: chatStore.rooms[message.chat]!.user.id,
        });
      }

      chatStore.rooms[message.chat]!.events = events;
      chatStore.rooms[message.chat]!.unreadEvents = unreadEvents;
    }

    chatStore.rooms = { ...chatStore.rooms };

    return Promise.resolve(textChatRoomItem);
  }

  return Promise.resolve(undefined);
};

export const transformTextChatRoomItemMessageWebSocketToTextChatRoomItem = (
  textChatRoomItemMessageWebSocket: TextChatRoomItemMessageWebSocket,
  userId = ""
) => {
  const isMine = userId === textChatRoomItemMessageWebSocket.user;
  const type = isMine ? ChatRoomItemType.OUTGOING_TEXT : ChatRoomItemType.INCOMING_TEXT;
  const createdAt = unixSecondsToDate(textChatRoomItemMessageWebSocket.created);
  const textChatRoomItem: TextChatRoomItem = {
    id: textChatRoomItemMessageWebSocket.eid,
    createdAt,
    text: textChatRoomItemMessageWebSocket.text,
    type,
    old: !!textChatRoomItemMessageWebSocket.old,
    isReceived: !!textChatRoomItemMessageWebSocket.is_received,
  };

  if (type === ChatRoomItemType.OUTGOING_TEXT) {
    textChatRoomItem.isConfirmed = true;
  }

  return textChatRoomItem;
};

export default {
  isType: isTextChatRoomItemType,
  handler: textChatRoomItemHandler,
} as EventFilter<ExtraParamsTextChatRoomItem>;
